import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext.tsx';
import { PhilanthropiesAPI } from '../services/philanthropies.ts';
import { formatCurrency } from '../utils/currency.ts';
import { philanthropyCategoryLabel } from '../utils/labels.ts';
import PageHeader from '../components/ui/PageHeader.tsx';
import ConfirmationModal from '../components/ConfirmationModal.tsx';
import StackCell from '../components/ui/StackCell.tsx';
import OpenAction from '../components/ui/OpenAction.tsx';

const Philanthropies: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const { data, isLoading, error } = useQuery('philanthropies', () => PhilanthropiesAPI.list());

  const filtered = useMemo(() => {
    return (data || []).filter((item: any) => {
      const q = searchTerm.toLowerCase();
      const matchesSearch = !q || String(item.description || '').toLowerCase().includes(q);
      const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [data, searchTerm, categoryFilter]);

  const total = filtered.reduce((s: number, i: any) => s + Number(i.amount || 0), 0);
  const donated = filtered.reduce((s: number, i: any) => s + Number(i.donatedAmount || 0), 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }
  if (error) return <div className="field-error">Erro ao carregar filantropia.</div>;

  return (
    <div>
      <PageHeader
        breadcrumb="Patrimônio / Filantropia"
        title="Filantropia"
        subtitle="Doações e instituições"
        actions={
          hasPermission('investments.create') || hasPermission('assets.create') ? (
            <button type="button" className="btn btn-primary" onClick={() => navigate('/philanthropies/novo')}>
              <PlusIcon style={{ width: 18, height: 18 }} />
              Novo
            </button>
          ) : undefined
        }
      />

      <div className="toolbar">
        <div className="filters">
          <label className="filter-field">
            <span className="filter-label">Categoria</span>
            <select className="select" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
              <option value="all">Todas</option>
              <option value="education">Educação</option>
              <option value="health">Saúde</option>
              <option value="environment">Meio Ambiente</option>
              <option value="other">Outros</option>
            </select>
          </label>
          <label className="filter-field" style={{ flex: 1 }}>
            <span className="filter-label">Buscar</span>
            <input
              className="input search"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Descrição"
            />
          </label>
        </div>
      </div>

      <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="kpi">
          <div className="kpi-label">Fundos</div>
          <div className="kpi-value">{filtered.length}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Dotação</div>
          <div className="kpi-value">{formatCurrency(total)}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Doado</div>
          <div className="kpi-value">{formatCurrency(donated)}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Filantropia</div>
          <span className="muted">{filtered.length} registros</span>
        </div>
        {filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center' }} className="muted">
            Nenhum registro encontrado.
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table compact-mobile">
              <thead>
                <tr>
                  <th>Descrição</th>
                  <th className="hide-m">Categoria</th>
                  <th className="hide-m" style={{ textAlign: 'right' }}>Dotação</th>
                  <th style={{ textAlign: 'right' }} className="hide-m">
                    Doado
                  </th>
                  <th style={{ textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item: any) => (
                  <tr key={item.id}>
                    <td>
                      <StackCell
                        title={item.description}
                        meta={philanthropyCategoryLabel(item.category)}
                        stackExtra={
                          <>
                            Dotação {formatCurrency(Number(item.amount || 0))} · Doado {formatCurrency(Number(item.donatedAmount || 0))}
                          </>
                        }
                      />
                    </td>
                    <td className="hide-m">{philanthropyCategoryLabel(item.category)}</td>
                    <td className="amount hide-m" style={{ textAlign: 'right' }}>
                      {formatCurrency(Number(item.amount || 0))}
                    </td>
                    <td className="amount hide-m" style={{ textAlign: 'right' }}>
                      {formatCurrency(Number(item.donatedAmount || 0))}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="row-actions">
                        <OpenAction onClick={() => navigate(`/philanthropies/${item.id}`)} />
                        {hasPermission('assets.delete') && (
                          <button
                            type="button"
                            className="link-btn action-keep action-danger"
                            onClick={() => { setDeleteId(item.id); setShowDeleteModal(true); }}
                          >
                            Excluir
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteId(null); }}
        title="Excluir Filantropia"
        message="Tem certeza que deseja excluir este registro? Esta ação não pode ser desfeita."
        onConfirm={async () => {
          if (deleteId == null) return;
          setDeleting(true);
          try {
            await PhilanthropiesAPI.remove(String(deleteId));
            queryClient.invalidateQueries(['philanthropies']);
          } finally {
            setDeleting(false);
            setShowDeleteModal(false);
            setDeleteId(null);
          }
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default Philanthropies;
